import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileSignature, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MOU_BODY, MOU_VERSION } from "@/lib/partnerMou";

type MouStatus = {
  mou_version: string | null;
  mou_accepted_at: string | null;
  mou_accepted_name: string | null;
  mou_accepted_title: string | null;
};

type Acceptance = {
  id: string;
  mou_version: string;
  full_name: string;
  title: string | null;
  accepted_at: string;
};

export default function MouTab({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [title, setTitle] = useState("");

  const statusQ = useQuery({
    queryKey: ["mou-status", orgId],
    queryFn: async (): Promise<MouStatus> => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("mou_version, mou_accepted_at, mou_accepted_name, mou_accepted_title")
        .eq("id", orgId)
        .single();
      if (error) throw error;
      return data as MouStatus;
    },
  });

  const historyQ = useQuery({
    queryKey: ["mou-history", orgId],
    queryFn: async (): Promise<Acceptance[]> => {
      const { data, error } = await supabase
        .from("partner_mou_acceptances")
        .select("id, mou_version, full_name, title, accepted_at")
        .eq("affiliate_id", orgId)
        .order("accepted_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Acceptance[];
    },
  });

  useEffect(() => {
    if (statusQ.data?.mou_accepted_name) setFullName(statusQ.data.mou_accepted_name);
    if (statusQ.data?.mou_accepted_title) setTitle(statusQ.data.mou_accepted_title);
  }, [statusQ.data]);

  const accept = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Sign in required");
      if (!fullName.trim()) throw new Error("Please type your full name");
      const userAgent = navigator.userAgent.slice(0, 400);
      const { error: insErr } = await supabase.from("partner_mou_acceptances").insert({
        affiliate_id: orgId,
        user_id: user.id,
        mou_version: MOU_VERSION,
        full_name: fullName.trim(),
        title: title.trim() || null,
        user_agent: userAgent,
      });
      if (insErr) throw insErr;
      const { error: updErr } = await supabase
        .from("affiliates")
        .update({
          mou_version: MOU_VERSION,
          mou_accepted_at: new Date().toISOString(),
          mou_accepted_by: user.id,
          mou_accepted_name: fullName.trim(),
          mou_accepted_title: title.trim() || null,
        })
        .eq("id", orgId);
      if (updErr) throw updErr;
    },
    onSuccess: () => {
      toast.success("Partner agreement accepted");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["mou-status", orgId] });
      qc.invalidateQueries({ queryKey: ["mou-history", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Could not save acceptance"),
  });

  const status = statusQ.data;
  const currentAccepted = status?.mou_accepted_at && status?.mou_version === MOU_VERSION;
  const previouslyAccepted = status?.mou_accepted_at && status?.mou_version !== MOU_VERSION;

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <ShieldCheck className={`h-8 w-8 ${currentAccepted ? "text-primary" : "text-muted-foreground"}`} />
            <div>
              <h3 className="font-display text-xl">Partner Agreement</h3>
              <p className="text-sm text-muted-foreground max-w-xl mt-1">
                Click-to-accept binds {orgName} to the Handbook standards. Legally valid under
                the U.S. E-SIGN Act.
              </p>
            </div>
          </div>
          <div className="text-right">
            {currentAccepted ? (
              <Badge className="bg-primary/15 text-primary border-primary/30">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Signed ({MOU_VERSION})
              </Badge>
            ) : previouslyAccepted ? (
              <Badge variant="outline" className="border-amber-500/50 text-amber-600">
                New version — re-sign required
              </Badge>
            ) : (
              <Badge variant="outline">Not signed</Badge>
            )}
          </div>
        </div>

        {currentAccepted && (
          <p className="text-xs text-muted-foreground">
            Signed by {status?.mou_accepted_name}
            {status?.mou_accepted_title ? `, ${status.mou_accepted_title}` : ""} on{" "}
            {new Date(status!.mou_accepted_at!).toLocaleDateString()}.
          </p>
        )}

        <div className="rounded-lg bg-muted/40 p-4 text-xs whitespace-pre-wrap font-mono max-h-72 overflow-y-auto">
          {MOU_BODY}
        </div>

        <div>
          <Button onClick={() => setOpen(true)}>
            <FileSignature className="h-4 w-4 mr-2" />
            {currentAccepted ? "Re-sign current agreement" : "Review & sign"}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h4 className="font-display text-sm uppercase tracking-wider text-muted-foreground mb-3">
          Signature history
        </h4>
        {historyQ.isLoading ? (
          <div className="py-6 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (historyQ.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No signatures on file yet.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {(historyQ.data ?? []).map((a) => (
              <li key={a.id} className="py-2 flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium">
                    {a.full_name}
                    {a.title ? `, ${a.title}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Version {a.mou_version} — {new Date(a.accepted_at).toLocaleString()}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px]">audit</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign partner agreement — {MOU_VERSION}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Type your full name and title. This creates a binding audit record.
            </p>
            <div>
              <Label>Full name</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" />
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Executive Director" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => accept.mutate()} disabled={accept.isPending || !fullName.trim()}>
              {accept.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Accept & sign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}